// ── Helper ───────────────────────────────────────────────
function tone(ctx: AudioContext, freq: number, delay: number, duration: number, volume: number, type: OscillatorType = 'sine') {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.frequency.setValueAtTime(freq, ctx.currentTime + delay)
  osc.type = type
  gain.gain.setValueAtTime(0, ctx.currentTime)
  gain.gain.setValueAtTime(volume, ctx.currentTime + delay)
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration)
  osc.start(ctx.currentTime + delay)
  osc.stop(ctx.currentTime + delay + duration)
}

// ── 1. Arpège majeur (actuel) ── C5 E5 G5 montant
export function soundArpege() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 523.25, 0, 1.2, 0.25)
    tone(ctx, 659.25, 0.15, 1.2, 0.25)
    tone(ctx, 783.99, 0.3, 1.2, 0.25)
  } catch { /* */ }
}

// ── 2. Double cloche ── deux coups graves + aigu
export function soundCloche() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 440, 0, 1.5, 0.3)
    tone(ctx, 880, 0.02, 0.8, 0.15)
    tone(ctx, 440, 0.5, 1.5, 0.25)
    tone(ctx, 880, 0.52, 0.8, 0.12)
  } catch { /* */ }
}

// ── 3. Xylophone ── notes descendantes rapides
export function soundXylophone() {
  try {
    const ctx = new AudioContext()
    const notes = [1046.5, 987.77, 880, 783.99, 659.25]
    notes.forEach((f, i) => tone(ctx, f, i * 0.1, 0.8, 0.2, 'triangle'))
  } catch { /* */ }
}

// ── 4. Doux ── une seule note douce avec harmonique
export function soundDoux() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 587.33, 0, 2, 0.2)       // D5
    tone(ctx, 1174.66, 0, 1.2, 0.08)   // D6 harmonique
  } catch { /* */ }
}

// ── 5. Victoire ── fanfare ascendante rapide
export function soundVictoire() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 523.25, 0, 0.3, 0.2, 'triangle')     // C5
    tone(ctx, 659.25, 0.15, 0.3, 0.2, 'triangle')   // E5
    tone(ctx, 783.99, 0.3, 0.3, 0.2, 'triangle')    // G5
    tone(ctx, 1046.5, 0.45, 1.5, 0.3, 'triangle')   // C6 tenu
  } catch { /* */ }
}

// ── 6. Carillon ── notes aléatoires douces type wind chime
export function soundCarillon() {
  try {
    const ctx = new AudioContext()
    const notes = [1318.51, 1174.66, 1046.5, 987.77, 880, 783.99]
    for (let i = 0; i < 6; i++) {
      const f = notes[i % notes.length]
      tone(ctx, f, i * 0.12, 1.5 - i * 0.15, 0.15 + Math.random() * 0.1)
    }
  } catch { /* */ }
}

// ── 7. Bulle ── son pop/bulle ludique
export function soundBulle() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(300, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(900, ctx.currentTime + 0.15)
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.4)
    // Second bubble
    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(400, ctx.currentTime + 0.25)
    osc2.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.4)
    gain2.gain.setValueAtTime(0, ctx.currentTime)
    gain2.gain.setValueAtTime(0.25, ctx.currentTime + 0.25)
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6)
    osc2.start(ctx.currentTime + 0.25)
    osc2.stop(ctx.currentTime + 0.6)
  } catch { /* */ }
}

// ── 8. Marimba ── deux notes boisées chaudes
export function soundMarimba() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 523.25, 0, 0.6, 0.3, 'triangle')
    tone(ctx, 783.99, 0.2, 0.6, 0.25, 'triangle')
    tone(ctx, 523.25, 0.2, 1.0, 0.1, 'sine') // résonance
  } catch { /* */ }
}

// ── 9. Glockenspiel ── aigu cristallin rapide
export function soundGlockenspiel() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 2093, 0, 1.5, 0.12)
    tone(ctx, 2637, 0.08, 1.2, 0.1)
    tone(ctx, 3136, 0.16, 1.0, 0.08)
  } catch { /* */ }
}

// ── 10. Levelup ── son de jeu vidéo rétro montant
export function soundLevelup() {
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'square'
    osc.frequency.setValueAtTime(330, ctx.currentTime)
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15)
    osc.frequency.exponentialRampToValueAtTime(990, ctx.currentTime + 0.3)
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.5)
  } catch { /* */ }
}

// ── 11. Harpe ── arpège doux et long
export function soundHarpe() {
  try {
    const ctx = new AudioContext()
    const notes = [261.63, 329.63, 392, 523.25, 659.25, 783.99]
    notes.forEach((f, i) => tone(ctx, f, i * 0.12, 2.5 - i * 0.2, 0.15))
  } catch { /* */ }
}

// ── 12. Ding ── simple ding net et court
export function soundDing() {
  try {
    const ctx = new AudioContext()
    tone(ctx, 1046.5, 0, 0.8, 0.3)
    tone(ctx, 2093, 0, 0.4, 0.1)
  } catch { /* */ }
}

// ── Expose sur window pour tester dans la console ────────
;(window as unknown as Record<string, unknown>).sons = {
  arpege: soundArpege,
  cloche: soundCloche,
  xylophone: soundXylophone,
  doux: soundDoux,
  victoire: soundVictoire,
  carillon: soundCarillon,
  bulle: soundBulle,
  marimba: soundMarimba,
  glockenspiel: soundGlockenspiel,
  levelup: soundLevelup,
  harpe: soundHarpe,
  ding: soundDing,
}
